import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PublicSite,
  RsvpHousehold,
  RsvpSubmission,
  UpdateSiteInput,
  Wedding,
} from '@guabi/shared';
import { apiGetPublic, apiPatch, apiPost } from '@/lib/api';

/* ---------- public, no account ---------- */

export const publicSiteKey = (slug: string) => ['public-site', slug] as const;
export const rsvpKey = (token: string) => ['rsvp', token] as const;

export function usePublicSite(slug: string) {
  return useQuery({
    queryKey: publicSiteKey(slug),
    queryFn: () => apiGetPublic<PublicSite>(`/public/site/${encodeURIComponent(slug)}`),
    // A guest opening a link has no session to refresh and nothing to re-check.
    retry: false,
  });
}

export function useRsvpHousehold(token: string) {
  return useQuery({
    queryKey: rsvpKey(token),
    queryFn: () => apiGetPublic<RsvpHousehold>(`/public/rsvp/${encodeURIComponent(token)}`),
    retry: false,
  });
}

export function useSubmitRsvp(token: string) {
  const qc = useQueryClient();
  return useMutation<{ saved: number }, Error, RsvpSubmission>({
    mutationFn: (body) =>
      apiPost<{ saved: number }>(`/public/rsvp/${encodeURIComponent(token)}`, body, {
        auth: false,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rsvpKey(token) }),
  });
}

/* ---------- the couple's own editing ---------- */

export function useUpdateSite(wedId: string) {
  const qc = useQueryClient();
  return useMutation<Wedding, Error, UpdateSiteInput & { updatedAt: string }>({
    mutationFn: (input) => apiPatch<Wedding>(`/weddings/${wedId}/site`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function usePublishSite(wedId: string) {
  const qc = useQueryClient();
  return useMutation<Wedding, Error, { slug?: string; sitePublished?: boolean }>({
    mutationFn: (input) => apiPost<Wedding>(`/weddings/${wedId}/site/publish`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/** Mints or replaces a household's invitation link. Replacing revokes the old one. */
export function useRsvpLink(wedId: string) {
  const qc = useQueryClient();
  return useMutation<{ token: string }, Error, { householdId: string }>({
    mutationFn: ({ householdId }) =>
      apiPost<{ token: string }>(`/weddings/${wedId}/households/${householdId}/rsvp-link`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['guests', wedId] }),
  });
}
